import clsx from "clsx";
import React from "react";
import { Box, Drawer, DrawerProps, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  NutritionTargetDto,
  NutritionEntryDto,
  RecomendedTargetBriefInfo,
} from "@growth-machine-llc/stridist-api-client";
import { NutritionHeader } from "../common/NutritionHeader";
import NutritionDrawerSectionTitle from "../common/NutritionDrawerSectionTitle";
import EntryView from "./EntryView";
import { CirclePlus } from "lucide-react";
import TerraConnectButton from "../../settings/TerraConnectButton";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { colorSystem } from "../../../theme";
import { Dayjs } from "dayjs";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDrawer-paper": {
      zIndex: theme.zIndex.nutritionDrawer,

      // margin for IOS devices
      marginTop: `calc(max(var(--safe-area-inset-top) - ${theme.spacing(2)}, 0px))`,
    },
  },

  header: {
    marginBottom: theme.spacing(2),
  },

  typeInfo: {
    marginBottom: theme.spacing(2),
  },

  paper: {
    padding: theme.spacing(5, 4),
    width: "100%",

    [theme.breakpoints.up("md")]: {
      width: theme.spacing(58),
      boxShadow: theme.shadows[8],
    },

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(5, 2),
    },
  },

  headerPadding: {
    [theme.breakpoints.down("sm")]: {
      paddingInline: theme.spacing(2),
    },
  },

  editContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },

  button: {
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
    padding: theme.spacing(1.5),
    width: "100%",
  },

  terraCard: {
    boxShadow: `0px 4px 4px 0px ${colorSystem.shadow1}`,
    border: `1px solid ${colorSystem.grayAlpha33}`,
    borderRadius: "12px",
    marginBottom: `${theme.spacing(3)} !important`,
    padding: theme.spacing(3),
  },

  terraButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export enum ClientNutritionTargetDrawerView {
  PAIRS = "PAIRS",
  EDIT = "EDIT",
}

export interface ClientNutritionTargetDrawerProps extends DrawerProps {
  nutritionTargets: NutritionTargetDto[];
  entries: NutritionEntryDto[];
  targetRecomendations: RecomendedTargetBriefInfo[];
  clientInfo: { clientId?: number; username?: string };
  isCoachView: boolean;
  date?: Dayjs;
}

type EntriesDrawerListMode = "SINGLE_DATE" | "EMPTY_LIST" | "LIST";

type EntriesPage = {
  pageItems: NutritionEntryDto[];
  pageNumber: number;
};

const ENTRIES_PER_PAGE = 14;

export function NewEntriesDrawer(props: ClientNutritionTargetDrawerProps) {
  const {
    className,
    clientInfo,
    onClose,
    open,
    nutritionTargets,
    entries: unsortedEntries,
    targetRecomendations,
    isCoachView,
    date: singleEntryDate,
  } = props;

  // include first page
  const [listPagesIncluded, setListPagesIncluded] = React.useState<number>(0);

  React.useEffect(() => {
    if (!open) {
      setListPagesIncluded(0);
    }
  }, [open]);

  const listMode: EntriesDrawerListMode = singleEntryDate
    ? "SINGLE_DATE"
    : unsortedEntries.length === 0
      ? "EMPTY_LIST"
      : "LIST";

  const entries = React.useMemo(() => {
    if (listMode === "SINGLE_DATE") {
      return unsortedEntries.filter((entry) =>
        entry.date.isSame(singleEntryDate, "day"),
      );
    }
    return unsortedEntries.sort((a, b) => {
      if (a.date.isAfter(b.date)) {
        return -1;
      }
      if (a.date.isBefore(b.date)) {
        return 1;
      }
      return 0;
    });
  }, [unsortedEntries, listMode, singleEntryDate]);

  const paginatedEntries: EntriesPage[] = React.useMemo(() => {
    const pages = [];
    for (let i = 0; i < entries.length; i++) {
      const pageNum = Math.floor(i / ENTRIES_PER_PAGE);
      if (!pages[pageNum]) {
        pages[pageNum] = {
          pageItems: [entries[i]],
          pageNumber: pageNum,
        };
      } else {
        pages[pageNum].pageItems.push(entries[i]);
      }
    }
    return pages;
  }, [entries]);

  const visibleList = React.useMemo(() => {
    return paginatedEntries
      .slice(0, listPagesIncluded + 1)
      .reduce((acc, page) => acc.concat(page.pageItems), []);
  }, [paginatedEntries, listPagesIncluded]);

  const recomendedTargets = React.useMemo(() => {
    if (!targetRecomendations?.length)
      return {} as Record<string, NutritionTargetDto>;
    return targetRecomendations.reduce(
      (acc, rec) => {
        const target = nutritionTargets.find(
          (t) => t.id === rec.recomendedTargetId,
        );
        if (target) {
          acc[rec.date.format("MM/DD/YYYY")] = target;
        }
        return acc;
      },
      {} as Record<string, NutritionTargetDto>,
    );
  }, [nutritionTargets, entries, targetRecomendations]);

  const itemRefs = React.useRef({});

  const getTodayEntry = () =>
    entries.find((entry) => entry.date.isSame(new Date(), "day"));

  const [expanded, setExpanded] = React.useState<number | "new" | undefined>(
    getTodayEntry()?.id || "new",
  );
  const s = useStyles();

  const me = useCurrentUser();

  const scrollToItem = (id) => {
    if (itemRefs.current[id]) {
      itemRefs.current[id].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleClose = () => {
    onClose({}, "backdropClick");
  };

  const handleOpenOtherEntry = (id: number) => {
    const entry = entries.find((entry) => entry.id === id);
    if (!entry) return;
    const onPage = paginatedEntries.find((page) =>
      page.pageItems.find((item) => item.id === id),
    );
    if (!onPage) return;
    if (listPagesIncluded < onPage.pageNumber) {
      setListPagesIncluded(onPage.pageNumber);
    }
    setExpanded(id);
    setTimeout(() => {
      scrollToItem(id);
    }, 10);
  };

  const handleAddedNewEntry = (forToday?: boolean) => {
    setExpanded(undefined);
    if (listMode === "SINGLE_DATE" || forToday) {
      onClose({}, "backdropClick");
    }
  };

  const handleUpdatedEntry = () => {
    setExpanded(undefined);
    if (listMode === "SINGLE_DATE") {
      onClose({}, "backdropClick");
    }
  };

  const hasTerraSyncedEntry = React.useMemo(() => {
    return unsortedEntries.some((entry) => entry.isLastModifiedAutomatically);
  }, [unsortedEntries]);

  return (
    <Drawer
      className={clsx(s.root, className)}
      classes={{
        paper: s.paper,
      }}
      anchor="right"
      variant="persistent"
      onClose={handleClose}
      open={open}
      children={
        <>
          <Box className={s.headerPadding}>
            <NutritionHeader
              className={s.header}
              onClose={handleClose}
              title={"Nutrition Entries"}
            />
          </Box>

          {/* List start */}
          {listMode === "SINGLE_DATE" && (
            <EntryView
              // always expanded if single entry mode
              expanded={true}
              showSummaryOnExpanded={true}
              entries={entries?.[0] ? [entries[0]] : []}
              entry={entries?.[0] || undefined}
              target={recomendedTargets?.[singleEntryDate.format("MM/DD/YYYY")]}
              clientInfo={clientInfo}
              onAddedNewEntry={handleAddedNewEntry}
              onUpdatedEntry={handleUpdatedEntry}
              onExpand={() => {}}
              onOpenOtherEntry={() => {}}
              hardDate={singleEntryDate}
            />
          )}

          {listMode === "EMPTY_LIST" && (
            <EntryView
              expanded={true}
              showSummaryOnExpanded={false}
              entries={entries}
              clientInfo={clientInfo}
              onExpand={() => {}}
              onAddedNewEntry={handleAddedNewEntry}
              onUpdatedEntry={handleUpdatedEntry}
              onOpenOtherEntry={handleOpenOtherEntry}
            />
          )}

          {listMode === "LIST" && (
            <>
              {expanded === "new" && (
                <>
                  <Box mb={2}>
                    <NutritionDrawerSectionTitle
                      title={"New nutrition entry"}
                    />
                  </Box>
                  <EntryView
                    expanded={true}
                    showSummaryOnExpanded={false}
                    entries={entries}
                    clientInfo={clientInfo}
                    onExpand={() => {}}
                    onOpenOtherEntry={handleOpenOtherEntry}
                    onAddedNewEntry={handleAddedNewEntry}
                    onUpdatedEntry={handleUpdatedEntry}
                  />
                </>
              )}
              <Box mb={2}>
                <NutritionDrawerSectionTitle
                  title={"History"}
                  action={{
                    name: "Add new entry",
                    onClick: () => {
                      setExpanded("new");
                    },
                    icon: <CirclePlus />,
                  }}
                />
              </Box>
              {visibleList.map((entry, index) => (
                <Box
                  ref={(el) => (itemRefs.current[entry.id] = el)}
                  key={entry.id}
                >
                  <EntryView
                    expanded={expanded === entry.id}
                    showSummaryOnExpanded={true}
                    entries={entries}
                    entry={entry}
                    target={
                      recomendedTargets?.[entry.date.format("MM/DD/YYYY")]
                    }
                    clientInfo={clientInfo}
                    onAddedNewEntry={handleAddedNewEntry}
                    onUpdatedEntry={handleUpdatedEntry}
                    onExpand={() => {
                      setExpanded((prev) =>
                        prev === entry.id ? undefined : entry.id,
                      );
                    }}
                    onOpenOtherEntry={handleOpenOtherEntry}
                  />
                </Box>
              ))}
            </>
          )}

          {visibleList.length < entries.length && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={2}
              onClick={() => {
                setListPagesIncluded((prev) => prev + 1);
              }}
              sx={{ cursor: "pointer" }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ cursor: "pointer" }}
              >
                Show older entries
              </Typography>
            </Box>
          )}

          {/* List end */}

          {!me.isImpersonating && !hasTerraSyncedEntry && !isCoachView && (
            <>
              <Box my={2}>
                <NutritionDrawerSectionTitle title={"Nutrition data syncing"} />
              </Box>
              <Box className={s.terraCard}>
                <Typography variant="body2" color="textSecondary">
                  Automatically sync your macros data from nutrition trackers
                  like MyFitnessPal, Chronometer or others.
                </Typography>
                <TerraConnectButton
                  connected={me.terraConnected}
                  className={s.terraButton}
                />
                {me.terraConnected && (
                  <Typography variant="caption">
                    Connected services:{" "}
                    {me.terraProviders
                      .map((provider) => provider.provider.toLowerCase())
                      .join(", ")}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      }
    />
  );
}
